import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getUsersAPI } from "@modules/apis/user-management";
import { AutocompleteUsers } from "@components_v2/autocomplete";
import { useSelector } from "react-redux";

export const UsersInput = ({
    user,
    defaultOptions,
    isClear,
    disabled = false,
    onDeleteUser,
    onAddUser,
    placeholder,
    isEdit,
    isAvatarIconRequired = false,
    sx,
    disabledSearchUsers = true,
}) => {
    const space = useSelector(({ stage }) => stage);
    const project = useSelector(({ project }) => project);

    const [value, setValue] = useState(user);
    const [open, setOpen] = useState(false);

    const [searchedValue, setSearchedValue] = useState(null); //현재 검색중인 이름
    const [userOptions, setUserOptions] = useState([]);
    const [selectedUser, setSelectedUser] = useState(user); //현재 선택한 유저(유저의 아이디만 popover로 넘겨주면 되서 자체력으로 autocomplete를 사용하도록 만들었습니다.)
    const [pendingUser, setPendingUser] = useState(false);
    const [debouncedValue, setDebouncedValue] = useState(null); //디바운싱된 검색어
    const userPage = useRef(0);
    const totalUser = useRef(0);
    const optionsList = useRef([]);
    const USER_LIMIT = 40;

    const onSearchUsers = async (params) => {
        return await getUsersAPI(space.id, space.endpoint, project.id, params);
    };

    const searchUsers = useCallback(
        async (isInitValue = false) => {
            try {
                if (isInitValue) {
                    userPage.current = 0;
                    totalUser.current = 0;
                }

                if (pendingUser || (totalUser.current > 0 && totalUser.current <= userPage.current * USER_LIMIT))
                    return;
                setPendingUser(true);
                const response = await onSearchUsers({
                    name: debouncedValue,
                    shouldRequestDetail: false,
                    offset: userPage.current,
                    limit: USER_LIMIT,
                });

                let newData = [];
                if (isInitValue) newData = response.data.results;
                else {
                    newData = [...optionsList.current, ...response.data.results];
                }

                setUserOptions(newData);
                optionsList.current = response.data.results;
                userPage.current += 1;
                totalUser.current = response.data.totalCount;
            } catch (error) {
                console.error(error);
            } finally {
                setPendingUser(false);
            }
        },
        [pendingUser, space, searchedValue, userOptions, value, debouncedValue],
    );

    const onHandleDeleteUser = useCallback((index) => {
        setSelectedUser(null);
        onDeleteUser();
    }, []);

    const onHandleSubmitUser = useCallback(
        (value) => {
            if (value !== searchedValue) {
                setSearchedValue(value);
            }
        },
        [searchedValue],
    );

    const onHandleFetchUser = useCallback(() => {
        if (!disabledSearchUsers) searchUsers();
    }, []);

    const onHandleClickOption = useCallback(
        (selected) => {
            if (selected) {
                const foundUser = userOptions.find((user) => user.id === selected.id);
                setSelectedUser({ ...foundUser });
                onAddUser({ id: foundUser?.id, name: foundUser?.name, email: foundUser?.username });
            } else {
                onDeleteUser();
            }
        },
        [userOptions, onAddUser],
    );
    const handleInputChange = (reason) => {
        if (reason === "clear") {
            setSelectedUser(null);
            setValue(null);
        }
    };

    const handleClose = () => {
        setOpen(false);
    };

    const options = useMemo(() => {
        return (
            userOptions?.map((user, idx) => ({
                name: user.name,
                id: user.id,
                email: user.username,
                lastOption: idx === userOptions.length - 1,
            })) || []
        );
    }, [userOptions, optionsList]);

    useEffect(() => {
        //페이지 마운트 직후 쿼리를 통한 유저정보 유지
        if (!user && !disabledSearchUsers) searchUsers(true); //최초 조회
        if (user?.id !== selectedUser?.id) setSelectedUser(user);
        if (user?.name) setValue(user?.name);
    }, [user]);

    useEffect(() => {
        if (!disabledSearchUsers) searchUsers(true);
    }, []);

    useEffect(() => {
        setSelectedUser(null);
        setValue(null);
    }, [isClear]);

    useEffect(() => {
        //실시간 검색 지연
        if (searchedValue === null) return;
        const debounce = setTimeout(() => {
            return setDebouncedValue(searchedValue);
        }, 300);
        return () => clearTimeout(debounce);
    }, [searchedValue]);

    useEffect(() => {
        if (debouncedValue === null || !!disabledSearchUsers) return;
        searchUsers(true);
    }, [debouncedValue]);

    useEffect(() => {
        if (!defaultOptions) return;
        if (userOptions.length === 0) setUserOptions(defaultOptions);
    }, [defaultOptions]);
    return (
        <AutocompleteUsers
            value={value}
            open={open}
            disabled={disabled}
            setValue={setValue}
            isEdit={isEdit}
            placeholder={placeholder}
            options={options}
            sx={sx}
            setOpen={setOpen}
            handleClose={handleClose}
            onFetchData={onHandleFetchUser}
            onInputChange={handleInputChange}
            onClickOption={onHandleClickOption}
            onHandleDeleteUser={onHandleDeleteUser}
            onSubmit={onHandleSubmitUser}
            setSelectedUser={setSelectedUser}
            isAvatarIconRequired={isAvatarIconRequired}
        />
    );
};
